/**
 * Email Asset Creation Component
 * Handles Newsletter, Promotional, Transactional emails
 */

import React, { useState } from 'react';
import { Mail, Send, Eye, ChevronLeft, ChevronRight, Upload, Smartphone, Monitor, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface EmailAssetProps {
  clientId?: number;
  planId?: number;
  projectId?: number;
  onComplete?: (contentId: number) => void;
  onCancel?: () => void;
}

interface EmailType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const emailTypes: EmailType[] = [
  { id: 'newsletter', name: 'Newsletter', description: 'Regular content updates', icon: <Mail className="w-5 h-5" />, color: 'bg-blue-500' },
  { id: 'promotional', name: 'Promotional', description: 'Sales and offers', icon: <Mail className="w-5 h-5" />, color: 'bg-green-500' },
  { id: 'transactional', name: 'Transactional', description: 'Order confirmations, receipts', icon: <Mail className="w-5 h-5" />, color: 'bg-purple-500' },
  { id: 'announcement', name: 'Announcement', description: 'Company news and updates', icon: <Mail className="w-5 h-5" />, color: 'bg-orange-500' },
];

export default function EmailAssetCreation({ clientId, planId, projectId, onComplete, onCancel }: EmailAssetProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedEmailType, setSelectedEmailType] = useState<EmailType | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [images, setImages] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const [emailConfig, setEmailConfig] = useState({
    campaignName: '',
    fromName: '',
    fromEmail: '',
    replyToEmail: '',
    sendDate: '',
    sendTime: '',
    listSegment: '',
  });

  const [emailContent, setEmailContent] = useState({
    subjectLine: '',
    preheader: '',
    headline: '',
    bodyHtml: '',
    bodyPlain: '',
    ctaText: '',
    ctaUrl: '',
    footerText: '',
  });

  const [subjectVariations, setSubjectVariations] = useState<string[]>(['']);
  const [settings, setSettings] = useState({
    mobileResponsive: true,
    trackOpens: true,
    trackClicks: true,
    utmTracking: true,
    personalizeSubject: false,
  });

  const handleImagesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setImages(Array.from(e.target.files));
    }
  };

  const addSubjectVariation = () => {
    if (subjectVariations.length < 5) {
      setSubjectVariations([...subjectVariations, '']);
    }
  };

  const updateSubjectVariation = (index: number, value: string) => {
    const updated = [...subjectVariations];
    updated[index] = value;
    setSubjectVariations(updated);
  };

  const removeSubjectVariation = (index: number) => {
    if (subjectVariations.length > 1) {
      setSubjectVariations(subjectVariations.filter((_, i) => i !== index));
    }
  };

  const getSpamScore = () => {
    // Simple spam score calculation based on content
    let score = 0;
    const spamWords = ['free', 'buy now', 'limited time', 'act now', 'click here', '!!!', '$$$'];
    const text = `${emailContent.subjectLine} ${emailContent.bodyHtml}`.toLowerCase();

    spamWords.forEach(word => {
      if (text.includes(word)) score += 10;
    });

    if (emailContent.subjectLine.includes('!!!!')) score += 20;
    if (emailContent.subjectLine.length > 70) score += 15;
    if (!emailContent.bodyPlain) score += 10;

    return Math.min(score, 100);
  };

  const handleSubmit = async () => {
    if (!selectedEmailType) return;

    setLoading(true);

    try {
      // Create content item
      const contentPayload = {
        type: 'email',
        content_subtype: selectedEmailType.id,
        title: emailConfig.campaignName,
        description: emailContent.subjectLine,
        status: 'draft',
        client_id: clientId,
        plan_id: planId,
        project_id: projectId,
        platform_config: JSON.stringify({
          email_type: selectedEmailType.id,
          config: emailConfig,
          content: emailContent,
          subject_variations: subjectVariations.filter(s => s.trim()),
          settings,
          spam_score: getSpamScore(),
        }),
      };

      const contentRes = await axios.post(`${API_BASE_URL}/content`, contentPayload);
      const contentId = contentRes.data.id;

      // Upload images
      if (images.length > 0) {
        for (const image of images) {
          const formData = new FormData();
          formData.append('file', image);
          formData.append('content_id', contentId.toString());
          formData.append('asset_type', 'image');
          formData.append('asset_group', 'supplementary');

          await axios.post(`${API_BASE_URL}/assets/upload`, formData);
        }
      }

      // Create workflow steps
      const workflowSteps = [
        { step_order: 1, step_name: 'Email Type Selection', step_type: 'metadata', status: 'completed' },
        { step_order: 2, step_name: 'Email Configuration', step_type: 'metadata', status: 'completed' },
        { step_order: 3, step_name: 'Email Content', step_type: 'content', status: 'completed' },
        { step_order: 4, step_name: 'Images & Assets', step_type: 'assets', status: 'completed' },
        { step_order: 5, step_name: 'Submit for Approval', step_type: 'approval', status: 'pending' },
      ];

      for (const step of workflowSteps) {
        await axios.post(`${API_BASE_URL}/content/${contentId}/workflow/steps`, step);
      }

      alert('Email campaign created successfully!');
      if (onComplete) onComplete(contentId);
    } catch (error) {
      console.error('Failed to create email asset:', error);
      alert('Failed to create email campaign');
    } finally {
      setLoading(false);
    }
  };

  const spamScore = getSpamScore();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Email Campaign</h1>
              <p className="text-gray-600 mt-1">Newsletters, promotional emails, and announcements</p>
            </div>
            {onCancel && (
              <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {[
              { num: 1, label: 'Type' },
              { num: 2, label: 'Setup' },
              { num: 3, label: 'Content' },
              { num: 4, label: 'Images' },
              { num: 5, label: 'Preview' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step.num}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:inline">{step.label}</span>
                </div>
                {i < 4 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: Email Type Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Select Email Type</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emailTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedEmailType(type)}
                    className={`p-6 rounded-lg border-2 transition text-left ${
                      selectedEmailType?.id === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-12 h-12 ${type.color} rounded-lg flex items-center justify-center mb-3 text-white`}>
                      {type.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900">{type.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                  </button>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedEmailType}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next: Email Setup
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Email Configuration */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Email Configuration</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={emailConfig.campaignName}
                  onChange={(e) => setEmailConfig({ ...emailConfig, campaignName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., December Newsletter 2024"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Name *</label>
                  <input
                    type="text"
                    value={emailConfig.fromName}
                    onChange={(e) => setEmailConfig({ ...emailConfig, fromName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Email *</label>
                  <input
                    type="email"
                    value={emailConfig.fromEmail}
                    onChange={(e) => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="noreply@yourcompany.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reply-To Email</label>
                <input
                  type="email"
                  value={emailConfig.replyToEmail}
                  onChange={(e) => setEmailConfig({ ...emailConfig, replyToEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="support@yourcompany.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Send Date</label>
                  <input
                    type="date"
                    value={emailConfig.sendDate}
                    onChange={(e) => setEmailConfig({ ...emailConfig, sendDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Send Time</label>
                  <input
                    type="time"
                    value={emailConfig.sendTime}
                    onChange={(e) => setEmailConfig({ ...emailConfig, sendTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">List Segment (optional)</label>
                <input
                  type="text"
                  value={emailConfig.listSegment}
                  onChange={(e) => setEmailConfig({ ...emailConfig, listSegment: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., All Subscribers, VIP Customers"
                />
              </div>

              {/* Settings */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">Email Settings</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.mobileResponsive}
                      onChange={(e) => setSettings({ ...settings, mobileResponsive: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-blue-900">Mobile Responsive Design</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.trackOpens}
                      onChange={(e) => setSettings({ ...settings, trackOpens: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-blue-900">Track Email Opens</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.trackClicks}
                      onChange={(e) => setSettings({ ...settings, trackClicks: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-blue-900">Track Link Clicks</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.utmTracking}
                      onChange={(e) => setSettings({ ...settings, utmTracking: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-blue-900">Add UTM Parameters</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!emailConfig.campaignName || !emailConfig.fromName || !emailConfig.fromEmail}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Email Content
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Email Content */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Email Content</h2>

              {/* Subject Lines (A/B Testing) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Subject Lines (A/B Testing)</label>
                  {subjectVariations.length < 5 && (
                    <button
                      onClick={addSubjectVariation}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      + Add Variation
                    </button>
                  )}
                </div>
                {subjectVariations.map((subject, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => updateSubjectVariation(index, e.target.value)}
                      maxLength={70}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={`Subject line ${index + 1}`}
                    />
                    {subjectVariations.length > 1 && (
                      <button
                        onClick={() => removeSubjectVariation(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-500 mt-1">
                  {subjectVariations[0]?.length || 0}/70 characters (recommended: 40-50)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preheader Text</label>
                <input
                  type="text"
                  value={emailContent.preheader}
                  onChange={(e) => setEmailContent({ ...emailContent, preheader: e.target.value })}
                  maxLength={100}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Preview text shown after subject line"
                />
                <p className="text-xs text-gray-500 mt-1">{emailContent.preheader.length}/100 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Headline</label>
                <input
                  type="text"
                  value={emailContent.headline}
                  onChange={(e) => setEmailContent({ ...emailContent, headline: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Main headline in email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Body (HTML)</label>
                <textarea
                  value={emailContent.bodyHtml}
                  onChange={(e) => setEmailContent({ ...emailContent, bodyHtml: e.target.value })}
                  rows={10}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="<p>Your email content here...</p>"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plain Text Version</label>
                <textarea
                  value={emailContent.bodyPlain}
                  onChange={(e) => setEmailContent({ ...emailContent, bodyPlain: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Plain text version for email clients that don't support HTML"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                  <input
                    type="text"
                    value={emailContent.ctaText}
                    onChange={(e) => setEmailContent({ ...emailContent, ctaText: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Shop Now, Learn More"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CTA URL</label>
                  <input
                    type="url"
                    value={emailContent.ctaUrl}
                    onChange={(e) => setEmailContent({ ...emailContent, ctaUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
                <textarea
                  value={emailContent.footerText}
                  onChange={(e) => setEmailContent({ ...emailContent, footerText: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Company address, unsubscribe link, etc."
                />
              </div>

              {/* Spam Score Warning */}
              {spamScore > 30 && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  spamScore > 60 ? 'bg-red-50 text-red-900' : 'bg-yellow-50 text-yellow-900'
                }`}>
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Spam Score: {spamScore}%</p>
                    <p className="text-sm mt-1">
                      Your email may trigger spam filters. Consider:
                    </p>
                    <ul className="text-sm mt-1 list-disc list-inside">
                      <li>Avoid excessive punctuation (!!!)</li>
                      <li>Reduce sales-heavy language</li>
                      <li>Add plain text version</li>
                      <li>Keep subject line under 50 characters</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={!subjectVariations[0] || !emailContent.bodyHtml}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Images
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Images & Assets */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Upload Images (Optional)</h2>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label htmlFor="images-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Images for email content (PNG, JPG, GIF)</p>
                  <input
                    id="images-upload"
                    type="file"
                    onChange={handleImagesUpload}
                    multiple
                    accept="image/*"
                    className="hidden"
                  />
                </div>

                {images.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {images.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Image Best Practices</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Keep total email size under 100KB for best deliverability</li>
                  <li>• Use web-optimized images (compress before upload)</li>
                  <li>• Include alt text for accessibility</li>
                  <li>• Test on multiple email clients</li>
                </ul>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(5)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Preview & Submit */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Preview & Submit</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewMode('desktop')}
                    className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
                  >
                    <Monitor className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPreviewMode('mobile')}
                    className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
                  >
                    <Smartphone className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Email Preview */}
              <div className="bg-gray-100 rounded-lg p-6">
                <div className={`mx-auto bg-white rounded-lg shadow-lg overflow-hidden ${
                  previewMode === 'mobile' ? 'max-w-sm' : 'max-w-2xl'
                }`}>
                  {/* Email Header */}
                  <div className="bg-gray-50 p-4 border-b">
                    <div className="text-xs text-gray-600 mb-1">From: {emailConfig.fromName} &lt;{emailConfig.fromEmail}&gt;</div>
                    <div className="font-semibold text-sm">{subjectVariations[0]}</div>
                    {emailContent.preheader && (
                      <div className="text-xs text-gray-500 mt-1">{emailContent.preheader}</div>
                    )}
                  </div>

                  {/* Email Body */}
                  <div className="p-6">
                    {emailContent.headline && (
                      <h1 className="text-2xl font-bold text-gray-900 mb-4">{emailContent.headline}</h1>
                    )}

                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: emailContent.bodyHtml || '<p class="text-gray-500">Email content will appear here...</p>' }}
                    />

                    {emailContent.ctaText && emailContent.ctaUrl && (
                      <div className="mt-6">
                        <a
                          href={emailContent.ctaUrl}
                          className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                        >
                          {emailContent.ctaText}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Email Footer */}
                  {emailContent.footerText && (
                    <div className="bg-gray-50 p-4 border-t">
                      <p className="text-xs text-gray-600 whitespace-pre-wrap">{emailContent.footerText}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-blue-600">Subject Variations</div>
                  <div className="text-2xl font-bold text-blue-900">{subjectVariations.filter(s => s.trim()).length}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-green-600">Images</div>
                  <div className="text-2xl font-bold text-green-900">{images.length}</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-purple-600">Has Plain Text</div>
                  <div className="text-2xl font-bold text-purple-900">{emailContent.bodyPlain ? 'Yes' : 'No'}</div>
                </div>
                <div className={`p-4 rounded-lg ${spamScore > 60 ? 'bg-red-50' : spamScore > 30 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                  <div className={`text-sm ${spamScore > 60 ? 'text-red-600' : spamScore > 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                    Spam Score
                  </div>
                  <div className={`text-2xl font-bold ${spamScore > 60 ? 'text-red-900' : spamScore > 30 ? 'text-yellow-900' : 'text-green-900'}`}>
                    {spamScore}%
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
